import { useMemo, useState } from 'react';
import { Search, Heart, MapPin, ArrowLeft, Home, ChevronRight } from 'lucide-react';
import type { Center } from '../memberData';
import { Card, EmptyState } from '../mobileUi';
import { useT } from '../../../i18n';

export interface MobileCentersProps {
  centers: Center[];
  favoriteId: string | null;
  /** null = show activities from every centre. */
  filterId: string | null;
  onFavorite: (id: string | null) => Promise<void> | void;
  onFilter: (id: string | null) => Promise<void> | void;
  /** Open a centre's own page (Om · Spill · Aktiviteter · Matcher). */
  onOpen?: (id: string) => void;
  onBack: () => void;
}

// «Padelsentre» — search the centres a member can play at, mark a favourite,
// choose whether the app shows activities from all of them or one, and open
// a centre's page. One club per install today; the list grows as activities
// name other venues.
export function MobileCenters({
  centers,
  favoriteId,
  filterId,
  onFavorite,
  onFilter,
  onOpen,
  onBack,
}: MobileCentersProps) {
  const t = useT();
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return centers.filter((c) => !n || c.name.toLowerCase().includes(n));
  }, [centers, q]);

  const chip = (active: boolean) =>
    active
      ? { background: '#0f1611', color: '#fff' }
      : { background: '#fff', color: '#0f1611', border: '1px solid #e7eae3' };

  return (
    <div className="px-4 pb-4 space-y-3">
      <div className="flex items-center gap-2 pt-2">
        <button
          onClick={onBack}
          aria-label={t('mobile.common.back')}
          className="p-1 -ml-1 text-ink-3"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="font-display font-semibold text-ink leading-tight" style={{ fontSize: 26 }}>
          {t('mobile.centres.title')}
        </h1>
      </div>
      <p className="text-[12px] text-ink-3 px-1">{t('mobile.centres.lead')}</p>

      <div
        className="flex items-center gap-2 rounded-xl px-3 py-2"
        style={{ background: '#eef0ea' }}
      >
        <Search size={15} className="text-ink-3" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('mobile.centres.search')}
          // The placeholder is a hint, not a name (#900).
          aria-label={t('mobile.centres.searchLabel')}
          className="flex-1 bg-transparent text-[14px] text-ink"
        />
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => onFilter(null)}
          className="rounded-full px-3 py-1.5 text-[12px] font-semibold"
          style={chip(filterId == null)}
        >
          {t('mobile.centres.all')}
        </button>
        <button
          onClick={() => favoriteId && onFilter(favoriteId)}
          disabled={!favoriteId}
          className="rounded-full px-3 py-1.5 text-[12px] font-semibold disabled:opacity-40"
          style={chip(filterId != null && filterId === favoriteId)}
        >
          {t('mobile.centres.favouriteOnly')}
        </button>
      </div>

      <div className="space-y-2">
        {list.map((c) => {
          const fav = c.id === favoriteId;
          const shown = filterId == null || filterId === c.id;
          return (
            <Card key={c.id} accent={fav ? '#c2f02f' : undefined}>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => onOpen?.(c.id)}
                  className="flex items-center gap-3 min-w-0 flex-1 text-left"
                  aria-label={t('mobile.centres.open', { name: c.name })}
                >
                  <div
                    className="flex items-center justify-center rounded-2xl flex-shrink-0"
                    style={{ width: 42, height: 42, background: '#eef0ea', color: '#0f1611' }}
                  >
                    {c.isHome ? <Home size={18} /> : <MapPin size={18} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-semibold text-ink truncate">{c.name}</div>
                    <div className="text-[11px] text-ink-3">
                      {c.isHome ? t('mobile.centres.yourClub') : ''}
                      {c.openCount === 1
                        ? t('mobile.centres.openActivity', { count: c.openCount })
                        : t('mobile.centres.openActivities', { count: c.openCount })}
                      {!shown ? t('mobile.centres.hiddenSuffix') : ''}
                    </div>
                  </div>
                  {onOpen && <ChevronRight size={16} className="text-ink-3 flex-shrink-0" />}
                </button>
                <button
                  onClick={() => onFavorite(fav ? null : c.id)}
                  aria-label={fav ? t('mobile.centres.unfavourite') : t('mobile.centres.favourite')}
                  aria-pressed={fav}
                  className="p-2 rounded-full flex-shrink-0"
                  style={{ color: fav ? '#65a30d' : 'var(--ink-3)' }}
                >
                  <Heart size={20} fill={fav ? '#c2f02f' : 'none'} />
                </button>
              </div>
            </Card>
          );
        })}
        {list.length === 0 && <EmptyState title={t('mobile.centres.noMatches')} />}
      </div>
    </div>
  );
}
